import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ProductListFiveColumnGridBigImage from 'Scripts/Pages/ProductListingPage/ProductListFiveColumnGridBigImage';
import { IRootState } from 'Scripts/store';

import {
    Facets,
    useLazyGetFiltrationQuery,
    useLazyGetLoadMoreCategoryProductsQuery,
} from 'Services/productFilterApi';

import Filter from 'Pages/ProductListingPage/Filter';
import ProductFilterTags from 'Pages/ProductListingPage/ProductFilterTags';
import ProductList from 'Pages/ProductListingPage/ProductList';
import ProductSorting from 'Pages/ProductListingPage/ProductSorting';
import SlideInFilter from 'Pages/ProductListingPage/SlideInFilter';
import BlockRenderer from 'Blocks/BlockRenderer';
import BreadCrumbs from 'Components/BreadCrumbs';
import Divider from 'Components/Divider';
import DividerWide from 'Components/DividerWide';
import Loader from 'Components/Loader';
import Markup from 'Components/Markup';
import ImageStripe from 'Components/PageElements/ImageStripe';
import { ProductListingPagePagination } from 'Components/ProductListingPagePagination';

const ProductListingPage = () => {
    const [getFiltration, { isFetching: productFilterIsFetching }] =
        useLazyGetFiltrationQuery();

    const [loadMoreProducts, { isFetching: loadMoreProductsIsFetching }] =
        useLazyGetLoadMoreCategoryProductsQuery();

    const productListRef = useRef<HTMLDivElement>(null);
    const {
        headerText,
        headerDescription,
        footerDescription,
        images,
        pagination,
        facetSearchResult,
        products,
        breadCrumbs,
        translations,
        displayExtraLargeProductImages,
        blockAreas,
        productListCreativeItems,
    } = useSelector((state: IRootState) => state.productListingPage);

    const [productsData, setProductsData] = useState(products);
    const [paginationData, setPaginationData] = useState(pagination);

    const handleLoadMoreProducts = (index: Facets) => {
        loadMoreProducts(index).then(result => {
            setProductsData(prevData => [...prevData, ...result.data]);
        });
    };

    const handleFilterProduct = (index: Facets) => {
        getFiltration(index).then(result => {
            setProductsData(result.data.products);
            setPaginationData(result.data.pagination);
        });
    };

    return (
        <div
            ref={productListRef}
            className="flex h-full w-full flex-col bg-merino"
        >
            {images?.length > 0 && <ImageStripe imageStripe={images[0]} />}
            <BreadCrumbs
                contentLinks={breadCrumbs.contentLinks}
                margin="mt-1 mb-3 lg:mt-3 lg:mb-8 pl-4.5"
            />
            {blockAreas?.first?.length > 0 && (
                <div className="mb-5 px-4.5 lg:px-14">
                    <BlockRenderer blocks={blockAreas.first} />
                </div>
            )}
            <div className="mx-auto w-full max-w-content px-4.5 lg:px-14">
                <div className="mb-4 flex w-full flex-col gap-1 lg:mb-7 lg:w-[600px] lg:gap-2">
                    {headerText && (
                        <h1 className="font-montserrat text-xl font-light leading-9 text-black lg:text-h3">
                            {headerText}
                        </h1>
                    )}
                    {headerDescription && (
                        <Markup
                            html={headerDescription?.value}
                            cssClass="prose-block leading-5 mb-2 font-light"
                        />
                    )}
                </div>
                {blockAreas?.second?.length > 0 && (
                    <div className="mb-5">
                        <BlockRenderer blocks={blockAreas.second} />
                    </div>
                )}
                <div className="block lg:hidden">
                    <DividerWide />
                </div>
                <div className="mb-1 mt-3 flex items-center justify-between gap-5 lg:mt-0">
                    {facetSearchResult.facetFilters?.length > 0 && (
                        <>
                            <Filter
                                className="hidden lg:block"
                                facetFilters={facetSearchResult.facetFilters}
                                getFiltration={handleFilterProduct}
                                isFetching={productFilterIsFetching}
                            />
                            <SlideInFilter
                                translations={facetSearchResult.translations}
                                className="block lg:hidden"
                                facetFilters={facetSearchResult.facetFilters}
                                getFiltration={handleFilterProduct}
                                isFetching={productFilterIsFetching}
                            />
                        </>
                    )}
                    <ProductSorting
                        sortTitle={facetSearchResult.translations.sortTitle}
                        sortCriteria={facetSearchResult.sortCriteria}
                        getFiltration={handleFilterProduct}
                        isFetching={productFilterIsFetching}
                    />
                </div>
                <div className="hidden lg:block">
                    <DividerWide />
                </div>
                <ProductFilterTags
                    pagination={pagination}
                    totalProductsCountTitle={
                        translations.totalProductsCountTitle
                    }
                    getFiltration={getFiltration}
                    facetSearchResult={facetSearchResult}
                />
                <div
                    className={`relative before:absolute before:z-[2] before:h-full before:w-full before:transition-all before:duration-layout ${
                        productFilterIsFetching || loadMoreProductsIsFetching
                            ? 'before:visibile before:bg-merino/10 before:backdrop-blur-sm'
                            : 'before:invisible before:bg-merino/0 before:backdrop-blur-none'
                    }`}
                >
                    <span
                        className={`absolute z-[3] grid h-full w-full transition-[visibility] duration-layout ${
                            productFilterIsFetching ||
                            loadMoreProductsIsFetching
                                ? 'visible'
                                : 'invisible'
                        }`}
                    >
                        <span>
                            <Loader className="!mt-0 !items-end" size="3rem" />
                        </span>
                    </span>
                    {displayExtraLargeProductImages ? (
                        <ProductListFiveColumnGridBigImage
                            products={productsData}
                            blockAreas={blockAreas}
                            productListCreativeItems={productListCreativeItems}
                        />
                    ) : (
                        <ProductList
                            products={productsData}
                            blockAreas={blockAreas}
                            productListCreativeItems={productListCreativeItems}
                        />
                    )}
                    <ProductListingPagePagination
                        pagination={paginationData}
                        loadMoreProductsText={
                            translations.paginationLoadMoreProductsText
                        }
                        allProductsLoadedText={
                            translations?.paginationAllProductsLoadedText
                        }
                        getLoadMoreProducts={handleLoadMoreProducts}
                        isFetching={loadMoreProductsIsFetching}
                    />
                </div>
            </div>
            {(blockAreas?.fourth || footerDescription) && (
                <Divider margin="mt-4 mb-4 lg:myb-8 lg:mt-6" />
            )}

            {blockAreas?.fourth?.length > 0 && (
                <div className="mb-5 px-4.5 lg:px-14">
                    <BlockRenderer blocks={blockAreas.fourth} />
                </div>
            )}
            {footerDescription && (
                <div className="mx-auto flex w-full max-w-content flex-col items-center px-4.5 lg:max-w-[75%] lg:px-14">
                    <Markup
                        html={footerDescription?.value}
                        cssClass="prose-block leading-4 mb-2 lg:max-w-1/2 font-light"
                    />
                </div>
            )}
        </div>
    );
};

export default ProductListingPage;
